import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useAuth } from "@/hooks/use-auth";
import {
  getMumHubEntitlements,
  getMumHubProfile,
  getMumHubStage,
  getMumHubWebConfig,
  MUM_HUB_ENTITLEMENTS_QUERY_KEY,
  MUM_HUB_ENTITLEMENTS_RECEIPTS_QUERY_KEY,
  MUM_HUB_NO_ENTITLEMENTS,
  MUM_HUB_PROFILE_QUERY_KEY,
  MUM_HUB_STAGE_QUERY_KEY,
  MUM_HUB_WEB_CLOSED,
  MUM_HUB_WEB_CONFIG_QUERY_KEY,
  MumHubSignedOutError,
  shouldRetryMumHubStage,
  type MumHubEntitlements,
  type MumHubProfile,
  type MumHubStageContent,
  type MumHubWebConfig,
} from "./api";
import { deriveMumHubHubState, type MumHubHubState } from "./hub-state";
import { deriveMumHubStage, deriveStageProgress } from "./stage";
import { apiSlugForStage } from "./stages";

/**
 * The React Query wiring for Mum Hub (MAR-219).
 *
 * ## `staleTime` is NOT Infinity here, and that is on purpose
 *
 * The app-wide client sets `staleTime: Infinity`, which is right for a product
 * catalogue and wrong for these four reads. Every one of them changes
 * underneath the page in a way she is meant to see within one session:
 *
 * - the entitlements change the moment a Stripe webhook lands, which is the
 *   whole point of the return flow;
 * - the stage's `access.preview` changes with them;
 * - the profile changes when she edits her date on another tab;
 * - the web config changes when Adrian turns the shop on.
 *
 * With the default she would pay, come back, and be told the stage is still
 * locked until she closed the tab. So each query names a short `staleTime` of
 * its own and the return flow invalidates them outright.
 *
 * ## Signed out is a value, not an error
 *
 * `useAuth()` already knows whether there is a session, so every authenticated
 * query is `enabled` on it rather than being fired and allowed to 401. A 401
 * that arrives anyway is still handled, because a session can expire between
 * the auth check and the fetch, but it is not the ordinary path.
 */

/** Long enough to survive a re-render, short enough to notice a webhook. */
const SHORT_STALE_MS = 15_000;

export function useMumHubProfile(): UseQueryResult<MumHubProfile> {
  const { customer } = useAuth();
  return useQuery({
    queryKey: MUM_HUB_PROFILE_QUERY_KEY,
    queryFn: getMumHubProfile,
    enabled: !!customer,
    staleTime: SHORT_STALE_MS,
    retry: (failureCount, error) =>
      !(error instanceof MumHubSignedOutError) && failureCount < 2,
  });
}

export function useMumHubEntitlements(): UseQueryResult<MumHubEntitlements> {
  const { customer } = useAuth();
  return useQuery({
    queryKey: MUM_HUB_ENTITLEMENTS_QUERY_KEY,
    // Wrapped rather than passed by reference: React Query hands its query
    // function a context object, and this one now takes options, so a bare
    // reference would read `receipts` off the context and be slow by accident.
    queryFn: () => getMumHubEntitlements(),
    enabled: !!customer,
    staleTime: SHORT_STALE_MS,
    retry: (failureCount, error) =>
      !(error instanceof MumHubSignedOutError) && failureCount < 2,
  });
}

/**
 * The same read, with Stripe receipt URLs on it (MAR-236).
 *
 * A separate query key rather than a parameter on the one above, because this
 * variant costs a Stripe call per paid stage and only the membership page
 * wants it. Sharing a cache entry would make the gate, the hub and the return
 * flow all wait for Stripe so that one page could show a link.
 */
export function useMumHubEntitlementsWithReceipts(): UseQueryResult<MumHubEntitlements> {
  const { customer } = useAuth();
  return useQuery({
    queryKey: MUM_HUB_ENTITLEMENTS_RECEIPTS_QUERY_KEY,
    queryFn: () => getMumHubEntitlements({ receipts: true }),
    enabled: !!customer,
    staleTime: SHORT_STALE_MS,
    retry: (failureCount, error) =>
      !(error instanceof MumHubSignedOutError) && failureCount < 2,
  });
}

/**
 * The prices, and whether the shop is open.
 *
 * Public and unauthenticated, because the landing page shows prices to someone
 * who has never signed in, and that page is the whole SEO argument for the
 * feature.
 */
export function useMumHubWebConfig(): UseQueryResult<MumHubWebConfig> {
  return useQuery({
    queryKey: MUM_HUB_WEB_CONFIG_QUERY_KEY,
    queryFn: getMumHubWebConfig,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useMumHubStageContent(
  slug: string | null,
): UseQueryResult<MumHubStageContent> {
  const { customer } = useAuth();
  return useQuery({
    queryKey: [MUM_HUB_STAGE_QUERY_KEY, slug],
    queryFn: () => getMumHubStage(slug as string),
    enabled: !!customer && !!slug,
    staleTime: SHORT_STALE_MS,
    // Wrapped rather than passed by reference: the predicate takes `unknown`,
    // which would make React Query infer `TError = unknown` and change the
    // hook's whole return type.
    retry: (failureCount, error) => shouldRetryMumHubStage(failureCount, error),
  });
}

export interface MumHubHub {
  state: MumHubHubState;
  entitlements: MumHubEntitlements;
  /**
   * The date on file, or null (MAR-247).
   *
   * Carried out of the hook because the paywall has to say how long the whole
   * journey lasts before she pays, and that length is twelve months less three
   * for every whole trimester behind her. The stage is already derived here; the
   * date itself is what `access.ts` needs, and re-reading the profile in the
   * page would be a second source for one fact.
   */
  dueDate: string | null;
  config: MumHubWebConfig;
  /** True while the config is still being fetched, so the paywall can wait. */
  configLoading: boolean;
  configFailed: boolean;
  refetchAll: () => void;
}

/**
 * Everything the hub page needs, in one call.
 *
 * The date is turned into a stage HERE and the stage into a content slug here,
 * so the page never touches either vocabulary. `now` is a real `new Date()`
 * because a component cannot be handed a clock, which is exactly why every
 * function it calls takes one and is tested without it.
 */
export function useMumHubHub(now: Date = new Date()): MumHubHub {
  const { customer, isLoading: authLoading } = useAuth();
  const profile = useMumHubProfile();
  const entitlements = useMumHubEntitlements();
  const config = useMumHubWebConfig();

  const dueDate = profile.data?.dueDate ?? null;
  const stageResult = dueDate ? deriveMumHubStage(dueDate, now) : null;
  const stage = stageResult?.kind === "stage" ? stageResult.stage : null;
  const slug = stage ? apiSlugForStage(stage) : null;
  const content = useMumHubStageContent(slug);

  const state = deriveMumHubHubState({
    signedIn: !!customer,
    profileLoaded: !authLoading && !!customer && profile.isSuccess,
    hasDueDate: !!dueDate,
    stageResult,
    stageProgress: dueDate ? deriveStageProgress(dueDate, now) : null,
    content: !slug
      ? { status: "idle" }
      : content.isSuccess
        ? { status: "success", data: content.data }
        : content.isError
          ? { status: "error", error: content.error }
          : { status: "pending" },
  });

  return {
    state:
      // A profile read that FAILED is not "she has no date": it is a fault, and
      // showing her the set-up page would invite her to answer a question she
      // has already answered, and then fail to save it.
      profile.isError && !!customer && !(profile.error instanceof MumHubSignedOutError)
        ? { kind: "unavailable" }
        : state,
    entitlements: entitlements.data ?? MUM_HUB_NO_ENTITLEMENTS,
    dueDate,
    config: config.data ?? MUM_HUB_WEB_CLOSED,
    configLoading: config.isPending,
    configFailed: config.isError,
    refetchAll: () => {
      void profile.refetch();
      void entitlements.refetch();
      void content.refetch();
      void config.refetch();
    },
  };
}
